import { memo } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Users, Home, ClipboardCheck, GraduationCap, FileText, Globe } from 'lucide-react'
import {
  APP_LANGUAGES,
  appLanguageDisplayLabel,
  resolveAppLanguageCode,
} from '@/i18n/app-languages'
import { APP_VERSION } from '@/lib/version'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from '@/components/ui/sidebar'

const base = '/mykoob-admin'

const navItems = [
  { titleKey: 'nav.userManagement', href: `${base}/users`, icon: Users },
  { titleKey: 'nav.testAssessment', href: `${base}/assessment`, icon: ClipboardCheck },
  { titleKey: 'nav.gradeValues', href: `${base}/grade-values`, icon: GraduationCap },
  { titleKey: 'nav.legal', href: `${base}/legal`, icon: FileText },
] as const

function NavLanguageSwitcher() {
  const { isMobile } = useSidebar()
  const { i18n } = useTranslation()
  const currentLabel = appLanguageDisplayLabel(i18n.language)

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              tooltip={currentLabel}
            >
              <Globe />
              <span>{currentLabel}</span>
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="min-w-36 rounded-lg"
            side={isMobile ? 'bottom' : 'right'}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuRadioGroup
              value={resolveAppLanguageCode(i18n.language)}
              onValueChange={(lang) => i18n.changeLanguage(lang)}
            >
              {APP_LANGUAGES.map((lang) => (
                <DropdownMenuRadioItem key={lang.code} value={lang.code}>
                  {lang.label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}

export const AppSidebar = memo(function AppSidebar() {
  const { t } = useTranslation()
  const location = useLocation()

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <NavLink to={base}>
                <div className="bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
                  <Home className="size-4" />
                </div>
                <div className="flex flex-col gap-0.5 leading-none">
                  <span className="font-semibold">{t('nav.appName')}</span>
                  <span className="text-muted-foreground text-xs">v{APP_VERSION}</span>
                </div>
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>{t('nav.navigation')}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {navItems.map((item) => (
                <SidebarMenuItem key={item.href}>
                  <SidebarMenuButton asChild isActive={location.pathname.startsWith(item.href)}>
                    <NavLink to={item.href}>
                      <item.icon />
                      <span>{t(item.titleKey)}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <NavLanguageSwitcher />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
})
